import React, { useMemo } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { useAuthContext } from 'AuthContext';
import { useMeetingHubContext } from '../../MeetingHubContext';
import Card from 'components/shared/Card';
import VotingTimerView from './VotingTimerView';
import VotingTable from '../VotingTable';
import VotingTimerActions from './VotingTimerActions';
import MeetingService from 'services/MeetingService';
import useRequest from 'hooks/useRequest';
import { useHubEvent } from 'hooks/useHubEvent';
import { Vote, VotingSession } from 'store/types/Meeting';
import MeetingHubEvents from 'store/enums/MeetingHubActions';
import { voteValueLabels } from 'util/meeting';

interface VotingSectionProps {
  votingSession: VotingSession;
}

const VotingSection: React.FunctionComponent<VotingSectionProps> = ({ votingSession }) => {
  const { isStaff } = useAuthContext();
  const { connection, meetingId, attendeeId, attendees, canViewAllVoters } = useMeetingHubContext();

  const getVotesRequest = useMemo(
    () => (!meetingId ? undefined : () => MeetingService.getVotes(meetingId, votingSession.motionId)),
    [meetingId, votingSession.motionId]
  );
  const { data: motionVotes = [], refetch: refetchMotionVotes } = useRequest<Vote[]>(getVotesRequest);

  const votingPercentage =
    votingSession.voters?.length && votingSession.voters?.length > 0
      ? Number((motionVotes.length / votingSession.voters?.length) * 100)
      : 0;

  const votesByAttendeeId = useMemo(() => {
    const map = new Map<string, Vote>();
    motionVotes.forEach((v) => map.set(v.attendeeId, v));
    return map;
  }, [motionVotes]);

  const sessionVoters = useMemo(
    () => attendees?.filter((a) => votingSession.voters?.includes(a.id)) ?? [],
    [attendees, votingSession.voters]
  );

  const currentUserVoter = useMemo(() => sessionVoters.find((v) => v.id === attendeeId), [attendeeId, sessionVoters]);
  const currentUserVote = useMemo(() => votesByAttendeeId.get(attendeeId)?.value, [attendeeId, votesByAttendeeId]);

  const visibleVoters = useMemo(() => {
    if (canViewAllVoters) return sessionVoters;
    return currentUserVoter ? [currentUserVoter] : [];
  }, [canViewAllVoters, sessionVoters, currentUserVoter]);

  useHubEvent(connection, MeetingHubEvents.VotesCleared, refetchMotionVotes);
  useHubEvent(connection, MeetingHubEvents.VoteCasted, refetchMotionVotes);

  return (
    <Card title={<Typography variant="h6">Voting</Typography>} bordered>
      {canViewAllVoters && (
        <LinearProgress
          variant="determinate"
          color={votingSession.isVotingAvailable ? 'secondary' : 'inherit'}
          value={votingPercentage}
        />
      )}
      {!currentUserVoter && !canViewAllVoters ? (
        <Typography sx={{ fontWeight: 700 }}>Voting in progress</Typography>
      ) : (
        <>
          <VotingTimerView />
          <VotingTable
            motionId={votingSession.motionId}
            disableVoting={!votingSession.isVotingAvailable}
            voters={visibleVoters}
            votesByAttendeeId={votesByAttendeeId}
          />
          {isStaff && <VotingTimerActions />}
        </>
      )}
      {currentUserVote && (
        <Box className="mt-3">
          <Typography variant="h6">Vote cast: {voteValueLabels[currentUserVote]}</Typography>
        </Box>
      )}
    </Card>
  );
};

export default VotingSection;
